import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getSavedJobs, unsaveJob } from '../../services/jobs';
import type { SavedJob } from '../../types/job';
import { JOB_TYPE_LABELS, LOCATION_TYPE_LABELS } from '../../types/job';

export function SavedJobsPage() {
  const [savedJobs, setSavedJobs] = useState<SavedJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  useEffect(() => {
    loadSavedJobs();
  }, []);

  const loadSavedJobs = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getSavedJobs();
      setSavedJobs(data);
    } catch (err) {
      console.error('Failed to load saved jobs:', err);
      setError('Failed to load saved jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (jobId: string) => {
    setRemovingId(jobId);

    try {
      await unsaveJob(jobId);
      setSavedJobs(prev => prev.filter(s => s.job_id !== jobId));
    } catch (err) {
      console.error('Failed to remove saved job:', err);
    } finally {
      setRemovingId(null);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-IE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatSalary = (job?: SavedJob['job']) => {
    if (!job || !job.show_salary || (!job.salary_min && !job.salary_max)) {
      return 'Competitive';
    }

    const currency = job.salary_currency || 'EUR';
    const formatter = new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    });

    if (job.salary_min && job.salary_max) {
      return `${formatter.format(job.salary_min)} - ${formatter.format(job.salary_max)}`;
    }
    if (job.salary_min) {
      return `From ${formatter.format(job.salary_min)}`;
    }
    if (job.salary_max) {
      return `Up to ${formatter.format(job.salary_max)}`;
    }
    return 'Competitive';
  };

  if (loading) {
    return (
      <div className="py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <div className="h-8 bg-gray-200 rounded w-40 mb-2 animate-pulse" />
            <div className="h-4 bg-gray-200 rounded w-56 animate-pulse" />
          </div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-lg" />
                  <div className="flex-1">
                    <div className="h-5 bg-gray-200 rounded w-2/3 mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-1/3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Saved Jobs</h1>
          <p className="text-gray-600 mt-1">Jobs you've bookmarked for later</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
            {error}
            <button onClick={loadSavedJobs} className="ml-2 underline">
              Try again
            </button>
          </div>
        )}

        {savedJobs.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <svg
              className="w-16 h-16 text-gray-400 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
              />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No saved jobs</h3>
            <p className="text-gray-600 mb-6">
              Save jobs you're interested in to view them later.
            </p>
            <Link
              to="/jobs"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700"
            >
              Browse Jobs
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {savedJobs.map(saved => (
              <div
                key={saved.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    {saved.job?.company_logo ? (
                      <img
                        src={saved.job.company_logo}
                        alt={saved.job.company_name}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 font-semibold">
                        {saved.job?.company_name?.charAt(0) || 'C'}
                      </div>
                    )}
                    <div>
                      <Link
                        to={`/jobs/${saved.job?.slug || saved.job_id}`}
                        className="text-lg font-semibold text-gray-900 hover:text-blue-600"
                      >
                        {saved.job?.title || 'Job Title'}
                      </Link>
                      <p className="text-sm text-gray-600">{saved.job?.company_name || 'Company'}</p>
                    </div>
                  </div>

                  <button
                    onClick={() => handleRemove(saved.job_id)}
                    disabled={removingId === saved.job_id}
                    className="p-2 text-gray-400 hover:text-red-500 disabled:opacity-50"
                    title="Remove from saved"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                    </svg>
                  </button>
                </div>

                {/* Job info */}
                <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-600">
                  {saved.job?.job_type && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {JOB_TYPE_LABELS[saved.job.job_type]}
                    </span>
                  )}
                  {saved.job?.location_type && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {LOCATION_TYPE_LABELS[saved.job.location_type]}
                    </span>
                  )}
                  <span>{formatSalary(saved.job)}</span>
                </div>

                {/* Notes */}
                {saved.notes && (
                  <div className="mt-3 p-3 bg-yellow-50 rounded-md text-sm text-yellow-800">
                    <strong>Your notes:</strong> {saved.notes}
                  </div>
                )}

                {/* Footer */}
                <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-xs text-gray-500">Saved: {formatDate(saved.created_at)}</span>
                  <div className="flex gap-4">
                    <Link
                      to={`/jobs/${saved.job?.slug || saved.job_id}`}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      View Details
                    </Link>
                    <Link
                      to={`/jobs/${saved.job?.slug || saved.job_id}/apply`}
                      className="text-sm font-medium text-blue-600 hover:text-blue-800"
                    >
                      Apply Now
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
